
class DrawerManager {
  #open = null;
  #trigger = null;
  #pressed = null;
  #autoOpened = false;

  constructor() {
    document.addEventListener('click', this.#onClick);
    // Il "click fuori" si giudica su dove e' iniziato il gesto: con mousedown dentro e mouseup fuori
    // (selezione di testo, trascinamento sul campo prezzo dei filtri) il click nasce sull antenato
    // comune, fuori dal cassetto, e lo chiuderebbe a meta' interazione.
    document.addEventListener('pointerdown', (event) => (this.#pressed = event.target), { capture: true });
    document.addEventListener('drawer:request-open', this.#onRequestOpen);
    document.addEventListener('keydown', (event) => event.key === 'Escape' && this.close());
    matchMedia('(max-width: 767px)').addEventListener('change', () => this.close());
    this.#initAutoOpen();
  }

  #panel(name) {
    return document.querySelector(`dialog[data-drawer="${name}"], [data-drawer="${name}"] dialog`);
  }

  #normalize(path) {
    try {
      return new URL(path, location.origin).pathname.replace(/\/$/, '');
    } catch {
      return path;
    }
  }

  open(name, trigger) {
    const el = this.#panel(name);
    if (!el || el === this.#open) return;
    this.close();
    // Il menu mobile e' un dialog modale: un cassetto non modale aperto da li' dentro resterebbe
    // dietro al suo velo, inerte. Si chiude il modale prima, e il focus alla fine tornera' sul
    // bottone del menu: il link che ha aperto il cassetto sta in un dialog ormai nascosto.
    const modale = document.querySelector('dialog:modal');
    if (modale) {
      modale.close();
      trigger = document.querySelector('[data-menu-toggle]') ?? trigger;
    }
    this.#open = el;
    this.#trigger = trigger ?? null;
    el.addEventListener('close', this.#onClose, { once: true });
    // Non modale: niente backdrop e niente inert, l header resta raggiungibile col cassetto aperto.
    el.show();
    this.#setExpanded('true');
  }

  #setExpanded(value) {
    if (this.#trigger?.hasAttribute('aria-expanded')) this.#trigger.setAttribute('aria-expanded', value);
  }

  // Dal proprio bottone (pill CART, Filtri) un secondo click chiude: con l header sopra il velo
  // quei controlli restano cliccabili, e un click che non fa nulla sembra rotto.
  #toggle(name, trigger) {
    if (this.#open && this.#open === this.#panel(name)) this.close();
    else this.open(name, trigger);
  }

  close() {
    if (!this.#open) return;
    // Senza modale il browser non riporta il focus da solo su chi ha aperto il cassetto. Solo se
    // il focus stava nel cassetto: chiudendo col click su un campo fuori, il campo deve tenerlo.
    const restituisci = this.#open.contains(document.activeElement);
    this.#open.close();
    this.#setExpanded('false');
    if (restituisci) this.#trigger?.focus({ preventScroll: true });
    this.#open = null;
    this.#trigger = null;
  }

  #onClose = (event) => {
    if (event.target === this.#open) this.close();
  };

  #onClick = (event) => {
    const inizio = this.#pressed ?? event.target;
    this.#pressed = null;
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      this.#toggle(opener.dataset.drawerOpen, opener);
      return;
    }
    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    // data-cart-page: il link deve proprio aprire la pagina carrello (il riepilogo B2B), non il cassetto
    const link = event.target.closest('a[href]:not([data-cart-page])');
    if (link) {
      const cartUrl = document.querySelector('[data-drawer="cart"]')?.dataset.cartUrl;
      if (cartUrl && this.#normalize(link.getAttribute('href')) === this.#normalize(cartUrl)) {
        event.preventDefault();
        this.#toggle('cart', link);
        return;
      }
    }
    if (this.#open && !this.#open.contains(inizio)) this.close();
  };

  #onRequestOpen = (event) => {
    const name = event.detail?.name;
    // chi ha il focus ora e' il bottone che ha chiesto l apertura (l ATC): alla chiusura si torna li
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (name) this.open(name, trigger);
  };

  #initAutoOpen() {
    for (const el of document.querySelectorAll('[data-autoopen]')) {
      const delay = parseInt(el.dataset.delay || '0', 10);
      if (delay <= 0) continue;
      const frequency = parseInt(el.dataset.frequency || '0', 10);
      const key = 'unimatic:autoopen:' + (el.dataset.drawer || el.dataset.autoopenDetails);
      const seen = localStorage.getItem(key);
      if (seen) {
        if (frequency <= 0) continue;
        const days = (Date.now() - parseInt(seen, 10)) / 86400000;
        if (days < frequency) continue;
      }
      setTimeout(() => {
        if (this.#open || this.#autoOpened || document.activeElement !== document.body) return;
        const target = el.dataset.autoopenDetails
          ? document.querySelector(el.dataset.autoopenDetails)
          : null;
        if (el.dataset.autoopenDetails && (!target || target.offsetParent === null)) return;
        this.#autoOpened = true;
        localStorage.setItem(key, String(Date.now()));
        if (target) {
          target.open = true;
          target.scrollIntoView({ block: 'nearest' });
        } else {
          this.open(el.dataset.drawer);
        }
      }, delay * 1000);
    }
  }
}

new DrawerManager();
